import { Fragment, useCallback, type ReactNode } from 'react'
import type { StaffKey, StaffLanguage, Vars } from '../lib/staffText'
import { showToast } from './toast'

// Bits the Floor and Appointments share: a phrase with some of its values drawn
// in bold, a calm duration, a short confirmation, and a list of names joined the
// way the reader's language joins them.

type Say = (key: StaffKey, vars?: Vars) => string

/**
 * A phrase with some of its values drawn as nodes — "<b>Tellers</b> is <em>12
 * min</em> over target." — without splitting the sentence into pieces a
 * translator cannot reorder. Each node stands in the phrase as a marker, and the
 * filled string is cut at the markers.
 */
export function rich(say: Say, key: StaffKey, vars: Vars, nodes: Record<string, ReactNode>): ReactNode {
  const marked: Vars = { ...vars }
  for (const name of Object.keys(nodes)) marked[name] = `\u0001${name}\u0001`
  const parts = say(key, marked).split('\u0001')
  return parts.map((part, i) => <Fragment key={i}>{i % 2 === 1 ? nodes[part] : part}</Fragment>)
}

/** A wait or a spell in whole minutes, as a person glancing at the floor reads
 *  it: "4 min", "2 h 05 min". Not the counter's ticking clock. */
export function mins(seconds: number, say: Say): string {
  const m = Math.max(0, Math.floor(seconds / 60))
  if (m < 60) return say('hall.min', { n: m })
  return say('hall.hmin', { h: Math.floor(m / 60), m: String(m % 60).padStart(2, '0') })
}

/** "Desk 1, Desk 2, Desk 9". Commas rather than Intl.ListFormat's "and": a
 *  browser without Azerbaijani locale data would write the English word (the
 *  reason formatMoment keeps its own month names). */
export function listNames(names: string[], _lang?: StaffLanguage): string {
  return names.join(', ')
}

/** A short confirmation, gone after a few seconds; `view` is null (App.tsx's region draws it). */
export function useHallToast() {
  const show = useCallback((next: string) => void showToast(next), [])
  return { show, view: null }
}

export function BellIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15z" />
      <path d="M10 20a2 2 0 0 0 4 0" />
    </svg>
  )
}
